// Standalone course landing page — route: /:token/:groupToken/:courseToken.
// A full-page sales page (decoupled from the community feed) that renders the
// owner-selected guruai template. Clicking Enroll opens the shared sign-in /
// register popup (same as the group landing); after auth the member is enrolled
// into the course's group and dropped straight onto the course.
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../config";
import PageLoader from "../PageLoader";
import AuthModal from "../AuthModal";
import { pickCourseTemplate } from "./index";

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", otp: "", password_confirmation: "", gdpr_consent: false };

// A course can only be entered directly when it sits in a FREE + PUBLIC group
// and is itself free. Anything else (paid group, private group, free-trial
// group, or a paid course) is "gated": the member is sent to the group landing
// page to get access / request to join / start a trial there. Returns null when
// the course is directly enterable, otherwise the alert copy to show.
function gateFor(group, course) {
  const pricing = group.pricing_type || "free";
  const isPrivate = Number(group.access_type) === 2;
  const coursePaid = Number(course?.access_type) === 2;
  const name = group.name || "this group";
  if (coursePaid) {
    return { kind: "Paid course", message: `“${course?.name || "This course"}” is a paid course inside ${name}. Head to the group to get access.`, cta: "Go to group" };
  }
  if (pricing === "paid") {
    return { kind: "Paid group", message: `This course is part of ${name}, a paid group. Get access to the group to unlock this course.`, cta: "Get access" };
  }
  if (pricing === "free_trial") {
    return { kind: "Free-trial group", message: `This course is inside ${name}, a free-trial group. Start your free trial on the group page to unlock this course.`, cta: "Start free trial" };
  }
  if (isPrivate) {
    return { kind: "Private group", message: `This course is inside ${name}, a private group. Request to join the group to unlock this course.`, cta: "Request to join" };
  }
  return null;
}

// Member persisted by the community app under cm_<community-token>.
const memberKey = (token) => `cm_${token}`;
function readMember(token) {
  try {
    return JSON.parse(localStorage.getItem(memberKey(token)) || "null");
  } catch {
    return null;
  }
}

export default function CourseLandingPage() {
  const { token, groupToken, courseToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [member, setMember] = useState(() => readMember(token));

  // Auth popup state (mirrors the group landing's AuthModal wiring).
  const [modal, setModal] = useState(null); // null | "join" | "signin" | "forgot"
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [forgotStep, setForgotStep] = useState(1);
  const [resetToken, setResetToken] = useState("");
  // A pending enroll intent: set when a guest clicks Enroll, consumed once the
  // member is authenticated so we can complete the enroll + redirect.
  const [pendingEnroll, setPendingEnroll] = useState(false);
  // Alert shown when the course is gated (paid/private/trial) — its button
  // routes to the group landing page.
  const [gate, setGate] = useState(null);
  const enrollingRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios
      .post(`${config.COMMUNITY_API_URL}/community-course-detail`, {
        community_token: token,
        course_token: courseToken,
        member_token: member?.auth_token,
        community_member_id: member?.community_member_id,
      })
      .then((res) => {
        if (!active) return;
        if (res.data?.error === 0) setData(res.data);
        else setError(res.data?.msg || "Course not found.");
      })
      .catch(() => active && setError("Something went wrong while loading the course."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, courseToken]);

  useEffect(() => {
    if (data?.course?.name) document.title = data.course.name;
  }, [data]);

  const saveMember = (m) => {
    setMember(m);
    try {
      localStorage.setItem(memberKey(token), JSON.stringify(m));
    } catch {
      /* ignore */
    }
  };

  const openModal = (name) => {
    setMsg({ type: "", text: "" });
    setForm(EMPTY_FORM);
    setForgotStep(1);
    setResetToken("");
    setModal(name);
  };
  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setPendingEnroll(false);
  };

  // ---- Enroll (member already authenticated). Logic 1: a free + public group
  // with a free course → join and land straight on the course. Logic 2: paid /
  // private / free-trial group, or a paid course → show an alert whose button
  // sends them to the group landing page to get access there. ----
  const enroll = async () => {
    if (enrollingRef.current) return;
    const group = data?.group;
    const course = data?.course;
    if (!group) {
      // No resolvable group — fall back to the community landing.
      navigate(`/${token}`);
      return;
    }

    // Logic 2 — gated course: show the alert instead of entering.
    const gated = gateFor(group, course);
    if (gated) {
      setGate(gated);
      return;
    }

    // Logic 1 — free public group: the shareable course view URL in the feed.
    const view = data?.can_view ? "&view=lessons" : "";
    const courseUrl = `/${token}?gid=${group.group_token}&page=courses&course=${courseToken}${view}`;

    // Already a member of the group → straight in.
    if (group.joined) {
      navigate(courseUrl);
      return;
    }

    // Otherwise join the group first (free public groups auto-join).
    enrollingRef.current = true;
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-group-join`, {
        community_token: token,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
        group_token: group.group_token,
      });
      if (res.data?.error === 0 && res.data.joined) {
        navigate(courseUrl);
      } else if (res.data?.pending) {
        toast.info(res.data.msg || "Request sent for admin approval.");
        navigate(`/${token}?gid=${group.group_token}`);
      } else if (res.data?.closed_group) {
        toast.error(res.data.msg || "You can't join this group.");
      } else {
        toast.error(res.data?.msg || "Could not enroll. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not enroll. Please try again.");
    } finally {
      enrollingRef.current = false;
    }
  };

  // The gate alert's button → group landing page (where the group's own Get
  // access / Request to join / Start trial flow lives).
  const goToGroup = () => {
    setGate(null);
    navigate(`/${token}?gid=${groupToken}`);
  };

  // Enroll CTA: guests authenticate first (the pendingEnroll effect resumes the
  // enroll once they're signed in); members enroll immediately.
  const onEnroll = () => {
    if (!member) {
      setPendingEnroll(true);
      openModal("join");
      return;
    }
    enroll();
  };

  // Resume a pending enroll the moment auth completes and the modal is gone.
  useEffect(() => {
    if (member && pendingEnroll && !modal) {
      setPendingEnroll(false);
      enroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, pendingEnroll, modal]);

  const submitJoin = async (e) => {
    e.preventDefault();
    const community = data?.community;
    if (community?.gdpr?.enabled && community?.gdpr?.mandatory && !form.gdpr_consent) {
      return setMsg({ type: "error", text: "Please accept the Privacy and Terms to continue." });
    }
    if (Number(community?.phone_required) === 1 && !String(form.phone || "").replace(/\D/g, "")) {
      return setMsg({ type: "error", text: "Please enter your phone number to continue." });
    }
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-join`, {
        community_token: token,
        group_token: groupToken,
        ref: new URLSearchParams(window.location.search).get("ref") || undefined,
        ...form,
      });
      if (res.data?.error === 0) {
        setModal(null);
        setForm(EMPTY_FORM);
        saveMember(res.data.member);
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Could not join." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-login`, {
        community_token: token,
        email: form.email,
        password: form.password,
      });
      if (res.data?.error === 0) {
        setModal(null);
        setForm(EMPTY_FORM);
        saveMember(res.data.member);
        toast.success("Successfully logged in!");
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Invalid email or password." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  // 3-step OTP reset: send code → verify code → set new password.
  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      if (forgotStep === 1) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-send-otp`, {
          community_token: token,
          email: form.email,
        });
        if (res.data?.success) {
          setResetToken(res.data.token || "");
          if (res.data.otp) setForm((f) => ({ ...f, otp: res.data.otp }));
          setForgotStep(2);
        } else {
          setMsg({ type: "error", text: res.data?.message || "Could not send the code." });
        }
      } else if (forgotStep === 2) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-verify-otp`, {
          community_token: token,
          email: form.email,
          otp: form.otp,
          token: resetToken,
        });
        if (res.data?.success) {
          setForgotStep(3);
        } else {
          setMsg({ type: "error", text: res.data?.message || "Invalid code." });
        }
      } else {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-reset-password`, {
          community_token: token,
          email: form.email,
          otp: form.otp,
          token: resetToken,
          password: form.password,
          password_confirmation: form.password_confirmation,
        });
        if (res.data?.success) {
          setForgotStep(1);
          setMsg({ type: "success", text: res.data?.message || "Your password has been reset." });
        } else {
          setMsg({ type: "error", text: res.data?.message || "Could not reset your password." });
        }
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.msg || "Something went wrong. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PageLoader label="Loading…" primary="#1daa61" />
      </div>
    );
  }
  if (error || !data?.course) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 px-4 text-center text-sm text-gray-500">
        <p>{error || "Course not found."}</p>
      </div>
    );
  }

  const landing = data.landing || {};
  const Template = pickCourseTemplate(landing.template);
  const primary = data.community?.primary_color || "#1daa61";

  return (
    <>
      <Template
        course={data.course}
        landing={landing}
        instructor={data.instructor}
        modules={Array.isArray(data.modules) ? data.modules : []}
        themeColor={(landing.theme?.color1 && String(landing.theme.color1).trim()) || ""}
        themeColor2={(landing.theme?.color2 && String(landing.theme.color2).trim()) || ""}
        canView={!!data.can_view}
        lockReason={data.lock_reason}
        policies={data.policies}
        labels={landing.labels || {}}
        member={member}
        communityToken={token}
        groupToken={groupToken}
        courseToken={courseToken}
        onEnroll={onEnroll}
      />

      {modal && (
        <AuthModal
          mode={modal}
          community={{ ...data.community, policies: data.policies }}
          primary={primary}
          form={form}
          setForm={setForm}
          busy={busy}
          msg={msg}
          forgotStep={forgotStep}
          onClose={closeModal}
          onSwitch={openModal}
          onSubmitJoin={submitJoin}
          onSubmitSignin={submitSignin}
          onSubmitForgot={submitForgot}
        />
      )}

      {gate && <EnrollGate gate={gate} primary={primary} onGo={goToGroup} onClose={() => setGate(null)} />}
    </>
  );
}

// Premium alert shown for a gated course (paid / private / free-trial). Its
// primary button routes to the group landing page.
function EnrollGate({ gate, primary, onGo, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
      >
        <div className="flex flex-col items-center px-7 pb-7 pt-8 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: `${primary}1A`, color: primary }}>
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" />
            </svg>
          </span>
          <span className="mt-4 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide" style={{ backgroundColor: `${primary}14`, color: primary }}>
            {gate.kind}
          </span>
          <h3 className="mt-3 text-lg font-bold text-gray-900">Access required</h3>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">{gate.message}</p>
          <button
            type="button"
            onClick={onGo}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5"
            style={{ backgroundColor: primary, boxShadow: `0 12px 30px ${primary}55` }}
          >
            {gate.cta}
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </button>
          <button type="button" onClick={onClose} className="mt-3 text-sm font-semibold text-gray-400 transition hover:text-gray-600">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
